import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { startFreeTrial } from '@/services/billing';
import { ABOUT_TRIAL_DISMISSED_KEY } from '@/constants/trial';

export function useTrialActivation() {
    const [isActivating, setIsActivating] = useState(false);
    const [activationError, setActivationError] = useState<string | null>(null);
    const queryClient = useQueryClient();

    const handleStartTrial = async () => {
        setIsActivating(true);
        setActivationError(null);

        try {
            const result = await startFreeTrial();
            if (!result.success) {
                console.error('Error activating trial:', result.error);
                setActivationError(result.error || 'Failed to start trial. Please try again.');
                return;
            }

            localStorage.setItem(ABOUT_TRIAL_DISMISSED_KEY, 'true');

            // The billing response is the source of truth for mutation success.
            // Prompt owners can close immediately; refreshes only reconcile the
            // rest of the shell and must never surface as a failed activation.
            void Promise.all([
                queryClient.invalidateQueries({ queryKey: ['userDetails'] }),
                queryClient.invalidateQueries({ queryKey: ['userTeams'] }),
                queryClient.invalidateQueries({ queryKey: ['totalBalance'] }),
                queryClient.invalidateQueries({ queryKey: ['personalBalance'] }),
                queryClient.invalidateQueries({ queryKey: ['teamCredits'] }),
                queryClient.invalidateQueries({ queryKey: ['userBootstrap'] }),
            ]).catch((error) => {
                console.warn('[Trial] Failed to invalidate post-activation cache', error);
            });

            return { success: true };
        } catch (error: any) {
            console.error('Error activating trial:', error);
            setActivationError(error?.message || 'Failed to start trial. Please try again.');
            return { success: false };
        } finally {
            setIsActivating(false);
        }
    };

    return {
        isActivating,
        activationError,
        handleStartTrial,
    };
}
